import React, { CSSProperties } from "react";
import styled, { css } from "styled-components";
import { SharedToggleProps } from "@shared/common/Toggle/types";
import useToggle from "@shared/common/Toggle/useToggle";
import { toggleStyles } from "@shared/common/Toggle/styles";
import {
  NEW_LOOK_PHONE_CONTROL,
  NEW_LOOK_PHONE_HIT,
  phoneMaxWidth,
} from "common/NewLook/layout";

interface SwitchSelectProps extends SharedToggleProps {
  optionStyle?: CSSProperties;
  style?: CSSProperties;
}

const SwitchSelect = ({
  options,
  selectedOption: propsSelectedOption,
  initialOption,
  onClick,
  disabled,
  invalidOptions,
  optionToDisplayName,
  optionStyle,
  style,
}: SwitchSelectProps) => {
  const { changeOption, selectedOption } = useToggle({
    options,
    initialOption,
    onClick,
    disabled,
    selectedOption: propsSelectedOption,
  });

  return (
    <Background $disabled={disabled} style={style}>
      {options?.map((option, i) => (
        <Option
          key={i}
          onClick={() => changeOption?.(option)}
          $selected={selectedOption === option}
          $disabled={disabled}
          $isValueSelected={!!selectedOption}
          $invalid={invalidOptions?.includes(option)}
          $isFirst={i === 0}
          $isLast={i === options.length - 1}
          style={{ padding: 8, ...optionStyle }}
        >
          {optionToDisplayName ? optionToDisplayName[option] : option}
        </Option>
      ))}
    </Background>
  );
};

export default SwitchSelect;

const Background = styled.div<{ $disabled?: boolean }>`
  ${toggleStyles.wrapper};
  border: ${(p) => p.theme.borders.medium};
  ${(p) =>
    p.$disabled &&
    `
    cursor: not-allowed;
  `};
  border-radius: 6px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      gap: 2px;
      padding: 3px;
      border: none;
      border-radius: ${p.theme.radii.control}px;
      background: ${p.theme.colors.cardLightBgColor};
      box-shadow: inset 0 0 0 1px ${p.theme.colors.cardBorderColor};
      /* Phone: the 36px control. */
      @media (max-width: ${phoneMaxWidth}) {
        height: ${NEW_LOOK_PHONE_CONTROL}px;
        padding: 2px;
      }
    `};
`;

const Option = styled.div<{
  $selected: boolean;
  $disabled?: boolean;
  $isValueSelected?: boolean;
  $invalid?: boolean;
  $isFirst?: boolean;
  $isLast?: boolean;
}>`
  ${toggleStyles.option};
  cursor: pointer;
  border-radius: 4px;

  ${(p) =>
    p.$disabled &&
    `
    cursor: not-allowed;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border-radius: ${p.theme.radii.control - 3}px;
      font-size: 13px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      color: ${p.theme.colors.secondaryText};
      transition: color 0.2s, background 0.2s;
      &:hover {
        color: ${p.theme.colors.titleColor};
      }
      ${p.$selected &&
      css`
        background: ${p.theme.colors.cardBgColor};
        color: ${p.theme.colors.titleColor};
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
        box-shadow: ${p.theme.shadows.dashboardCard},
          inset 0 0 0 1px ${p.theme.colors.cardBorderColor};
      `};
      /* Phone: 14px labels, weight by state, a 40px hit area. */
      @media (max-width: ${phoneMaxWidth}) {
        position: relative;
        font-size: 14px;
        font-weight: ${p.$selected ? 500 : 450};
        &::before {
          content: "";
          position: absolute;
          inset: -${(NEW_LOOK_PHONE_HIT - NEW_LOOK_PHONE_CONTROL) / 2 + 2}px 0;
        }
      }
    `};
`;
